import { SearchPattern } from "@novorender/data-js-api";

import { PipelineConfig, ProjectInfo } from "apis/dataV2/projectTypes";
import { BackendProvider } from "config/backend";
import { CanvasContextMenuFeatureKey } from "config/canvasContextMenu";
import { ButtonKey, FeatureGroupKey, WidgetKey } from "config/features";
import { SelectionResolution } from "features/render/types";
import { UnitSystem } from "features/uom/spec";
import type { Viewpoint } from "features/viewpoints/types";
import { DeepMutable } from "types/misc";

export enum SceneType {
    Viewer,
    Admin,
}

export enum ProjectType {
    V1, // app.novorender.com
    V2, // projects.novorender.com
}

export enum UxFlavor {
    Classic = "classic",
    New = "new",
    Procore = "procore",
    ProcoreAndroid = "procoreAndroid",
    ProcoreIOS = "procoreIOS",
}

export enum Platform {
    Web = "web",
    Android = "android",
    iOS = "ios",
}

export type UrlSearchQuery = {
    pattern: string | SearchPattern[];
    options: { selectionOnly: string; openWidgets: boolean; includeDescendants: boolean };
};
export type MutableUrlSearchQuery = DeepMutable<UrlSearchQuery>;

export type State = {
    isOnline: boolean;
    enabledWidgets: WidgetKey[];
    lockedWidgets: WidgetKey[];
    sceneType: SceneType;
    projectType: ProjectType;
    projectV2Info: ProjectInfo;
    pipelineConfig: PipelineConfig | undefined;
    tmZoneForCalc: string | undefined;
    requireConsent: boolean;
    organization: string;
    projectName: string;
    /** `InitOptions.sceneTitle`; empty when the host passed none. */
    hostSceneTitle: string;
    isVersionComparison: boolean;
    currentProjectVersion: string | null;
    widgets: WidgetKey[];
    favoriteWidgets: WidgetKey[];
    widgetSlot: {
        open: boolean;
        group: FeatureGroupKey | undefined;
    };
    widgetLayout: {
        widgets: number;
        sideBySide: boolean;
        padWidgetsTop?: boolean;
    };
    widgetGroupPanelState: {
        open: boolean;
        expanded: boolean;
    };
    maximized: WidgetKey[];
    maximizedHorizontal: WidgetKey[];
    minimized: WidgetKey | undefined;
    primaryMenu: {
        button1: ButtonKey;
        button2: ButtonKey;
        button3: ButtonKey;
        button4: ButtonKey;
        button5: ButtonKey;
    };
    contextMenu: {
        canvas: {
            features: CanvasContextMenuFeatureKey[];
        };
    };
    urlSearchQuery: MutableUrlSearchQuery | undefined;
    urlBookmarkId: string | undefined;
    localBookmarkId: string | undefined;
    config: {
        dataV2ServerUrl: string;
        projectsUrl: string;
        authServerUrl: string;
        bimCollabClientId: string;
        bimTrackClientId: string;
        jiraClientId: string;
        xsiteManageClientId: string;
        novorenderClientId: string;
        assetsUrl: string;
        mixpanelToken: string;
        uxFlavor: string;
    };
    uxFlavor: UxFlavor;
    backendProvider: BackendProvider;
    procoreContext: {
        companyId: number;
        projectId: number;
        sceneVersionId?: string;
        sceneSliceId?: string;
        enableFullSync?: boolean;
        nodeClassSelection?: boolean;
        spaceMouse?: boolean;
        objectTreeV2?: boolean;
        objectMeasure?: boolean;
        disableIncrementalCaching?: boolean;
        enableBimIndex?: boolean;
        enableMinimapTiling?: boolean;
        gpuGuidance?: boolean;
        hideSpacesRooms?: boolean;
    } | null;
    initViewpointId: string | null;
    /**
     * Viewpoint id supplied via `InitOptions.homeViewpointId`.
     * When set, it is loaded as the home viewpoint instead of the BIM model home view
     * resolved from the backend.
     */
    initHomeViewpointId: string | null;
    /**
     * Viewpoint object supplied via `InitOptions.viewpoint`.
     * When set, takes precedence over `initViewpointId` and the resolved BIM model home view —
     * the viewpoint is applied as the initial view.
     */
    initViewpoint: Viewpoint | null;
    appliedViewpoint: {
        viewpoint: { id?: string; name?: string } | null;
        appliedBy: "initialization" | "set-viewpoint" | "viewer-interaction";
    };
    isApplyingViewpoint: boolean;
    snackbarMessage: {
        msg: string;
        closeAfter?: number;
        link?: string;
        key: string;
    } | null;
    highlightSetting: null | {
        route: string;
        accordion?: string;
        field: string;
    };
    globalSearch: {
        open: boolean;
    };
    unitSystem: UnitSystem;
    selectionResolution: SelectionResolution;
    /**
     * Whether class-aware (NodeClass) selection resolution is enabled. Off by default; turned on
     * by the host/embedder (e.g. behind a LaunchDarkly flag) via scene `customProperties`. When
     * off, selection resolves with the pre-NodeClass behavior.
     */
    nodeClassSelection: boolean;
    /**
     * Whether the SpaceMouse setting is available. Off by default; turned on by the host/embedder
     * (e.g. behind a LaunchDarkly flag) via scene `customProperties`. When off, the setting is
     * hidden and the viewer never reaches for the local 3Dconnexion driver.
     */
    spaceMouse: boolean;
    cameraIsTooFar: null | "initial" | "flyTo";
    showUnusuallyFarObjects: boolean;
    sceneId: string | null;
    showWeakDeviceMessage: boolean;
    newProjectVersionStatus: {
        available: boolean;
        dismissed: boolean;
    };
    /**
     * Set when the BIM Search DB responds `202 Accepted` for the current scene —
     * its search database is missing from the shared read path and is being
     * backfilled from FAS. Drives a global "preparing" banner + recovery poll.
     */
    sceneDbBackfill: {
        preparing: boolean;
        // Set once the recovery poll observes the scene DB is available again. The
        // banner then switches to a "ready, reload to view" state instead of the
        // app reloading on its own (reload stays user-initiated).
        ready?: boolean;
    };
    joysticks: {
        moving: boolean;
    };
    uiConfig: {
        overlayElementsZIndex?: number;
    };
};

export type PrimaryMenuConfigType = State["primaryMenu"];

export type PositionedWidgetState = {
    key: WidgetKey;
    x: number;
    y: number;
    width: number;
    height: number;
};
